import { Component, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import { MessageService } from 'primeng/api'
import { DOCUMENT } from '@angular/common'
import { forkJoin } from 'rxjs'

import { CommitteeLeaseRow, CreateSessionDto, SessionKind } from '../../data/interfaces/committee-session.interface'
import { ApiLeaseService } from '@modules/private/lease/data/services/api/api-lease.service'
import { ApiCommitteeService } from '@data/services/modules/committee/api-committee.service'
import { CommitteeMember } from '@modules/private/lease/data/interfaces/committee.interface'
import { LeaseGroup, SESSION_KIND_LABEL } from '../../data/constants/committee.constants'
import { CommitteeShellService } from '../../data/services/view/committee-shell.service'
import { fullName } from '../../data/utils/committee-tally.util'
import { hasOpenOverlay } from '../../data/utils/overlay.util'

/** Lo que el modal entrega al cascarón al crear: el id para navegar y si el aviso por correo falló para alguien. */
export interface SessionCreated {
  id: string
  unnotified: number
}

/** Un arrendamiento elegido para la sesión, con lo justo para reconocerlo en el chip. */
export interface LeaseChip {
  _id: string
  folio: string
  clientName: string
  assetName: string
}

/**
 * El modal de nueva sesión: tipo, fecha, votantes, arrendamientos, observaciones y acta opcional.
 *
 * **El tipo lo sugiere el origen** (D-2): desde Por originar se abre una VOTACIÓN —hay que decidir—;
 * desde Activos y Cerrados un REGISTRO de acta —el contrato ya está vivo o liquidado, se documenta—.
 * En Cerrados la votación ni se ofrece: votar sobre un liquidado no significa nada.
 *
 * **Los arrendamientos llegan por id** (`?llevar=id,id`). Si la tabla los dejó preparados en el
 * cascarón se pintan de ahí; si no —recarga de la pestaña, enlace desde el expediente— se resuelven
 * uno por uno. Los demás se agregan buscando por folio.
 *
 * `[dismissableMask]="false"` a propósito (ui-styleguide §3.12, la excepción): hay trabajo sin guardar
 * y un clic fuera no debe tirarlo. X y Escape siguen cerrando.
 */
@Component({
  selector: 'app-new-session-dialog',
  templateUrl: './new-session-dialog.component.html',
  styleUrls: ['./new-session-dialog.component.scss'],
})
export class NewSessionDialogComponent implements OnChanges {
  @Input() visible = false
  @Input() preselectedIds: string[] = []
  @Input() origin: LeaseGroup = 'originating'
  @Output() visibleChange = new EventEmitter<boolean>()
  /** La sesión recién creada y a cuántos votantes NO se les pudo avisar (0 en registro o si todo salió). */
  @Output() created = new EventEmitter<SessionCreated>()

  kind: SessionKind = 'voting'
  kindOptions: { label: string; value: SessionKind }[] = []

  sessionDate: Date | null = new Date()
  /** El tope del calendario: no hay agendamiento (ADR-0013 #10), la sesión ya ocurrió u ocurre ahora. */
  today = new Date()

  selectedVoters: string[] = []
  // Opciones ESTABLES para el p-multiSelect (referencia fija): un getter devolvería un array nuevo
  // en cada change detection y el overlay entraría en bucle (lección de `lease-committee`).
  memberOptions: { label: string; value: string }[] = []
  membersLoaded = false

  leases: LeaseChip[] = []
  suggestions: CommitteeLeaseRow[] = []
  /** Lo que hay escrito en el buscador de folio; se limpia al elegir. */
  leaseQuery: unknown = null

  generalComments = ''
  minuteUrl: string | null = null

  submitting = false
  resolving = false

  readonly kindLabel: Record<string, string> = SESSION_KIND_LABEL

  constructor(
    private readonly api: ApiCommitteeService,
    private readonly apiLease: ApiLeaseService,
    private readonly shell: CommitteeShellService,
    private readonly messageService: MessageService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) this.prepare()
  }

  get isVoting(): boolean {
    return this.kind === 'voting'
  }

  /** Lo que el backend va a exigir, para no mandar algo que va a rebotar (la validación real es suya). */
  get canSubmit(): boolean {
    if (this.submitting || this.resolving || !this.leases.length) return false
    if (!this.sessionDate || this.sessionDate.getTime() > Date.now()) return false

    return !this.isVoting || this.selectedVoters.length > 0
  }

  /** Cada apertura parte de cero: el tipo por el origen, la fecha de ahora, todos los miembros convocados. */
  prepare(): void {
    this.today = new Date()
    this.sessionDate = new Date()
    this.generalComments = ''
    this.minuteUrl = null
    this.leaseQuery = null
    this.suggestions = []
    this.submitting = false

    this.kindOptions =
      this.origin === 'closed'
        ? [{ label: SESSION_KIND_LABEL['documentary'], value: 'documentary' }]
        : [
            { label: SESSION_KIND_LABEL['voting'], value: 'voting' },
            { label: SESSION_KIND_LABEL['documentary'], value: 'documentary' },
          ]
    this.kind = this.origin === 'originating' ? 'voting' : 'documentary'

    this.loadMembers()
    this.loadPreselected()
  }

  onKindChange(): void {
    //* Al volver a votación se convoca de nuevo a todos: es el default y lo que casi siempre se quiere.
    if (this.isVoting && !this.selectedVoters.length) this.selectedVoters = this.memberOptions.map((m) => m.value)
  }

  /** Busca por folio o cliente dentro del grupo de origen; lo ya elegido no se vuelve a ofrecer. */
  search(event: { query: string }): void {
    const q = (event?.query ?? '').trim()
    if (!q) {
      this.suggestions = []
      return
    }
    const chosen = new Set(this.leases.map((l) => l._id))
    this.api.getLeases(this.origin, 'all', 1, 10, q).subscribe((r) => {
      this.suggestions = r.error ? [] : (r.data?.data ?? []).filter((row) => !chosen.has(row._id))
    })
  }

  addLease(row: CommitteeLeaseRow): void {
    if (row && !this.leases.some((l) => l._id === row._id)) this.leases.push(this.toChip(row))
    //* El autocompletado deja el objeto elegido en el input; se limpia para el siguiente folio.
    this.leaseQuery = null
  }

  removeLease(id: string): void {
    this.leases = this.leases.filter((l) => l._id !== id)
  }

  onMinuteSelected(url: string): void {
    this.minuteUrl = url || null
  }

  onMinuteDeleted(): void {
    this.minuteUrl = null
  }

  submit(): void {
    if (!this.canSubmit || !this.sessionDate) return

    const dto: CreateSessionDto = {
      kind: this.kind,
      leases: this.leases.map((l) => l._id),
      sessionDate: this.sessionDate.toISOString(),
    }
    if (this.isVoting) dto.selectedVoters = this.selectedVoters
    if (this.generalComments.trim()) dto.generalComments = this.generalComments.trim()
    //* `minutes` solo si hay acta: un arreglo vacío no dice nada y el DTO lo valida elemento a elemento.
    if (this.minuteUrl) dto.minutes = [{ file: this.minuteUrl }]

    this.submitting = true
    this.api.createSession(dto).subscribe((r) => {
      this.submitting = false
      const id = r.data?.session?._id
      if (r.error || !id) {
        this.messageService.add({ severity: 'error', summary: 'No se creó la sesión', detail: r.msg || 'Revisa los datos e inténtalo de nuevo.' })
        return
      }
      //* Un rechazo de SendGrid no aborta la creación: el backend lo reporta por destinatario y aquí se
      //* cuenta para que el aviso lo diga. `notification` es `null` en registro: no hay a quién avisar.
      const unnotified = (r.data?.notification?.recipients ?? []).filter((x) => !x.emailSent).length
      //* Aquí NO se cierra: el padre navega al detalle y la ruta suelta `?llevar=`, que es lo que apaga
      //* el modal. Cerrarlo desde aquí disparaba un segundo `navigate` sobre la lista que pisaba al del
      //* detalle (QA-08).
      this.created.emit({ id, unnotified })
    })
  }

  /**
   * PrimeNG ya puso `visible=false` cuando el cierre viene de la X o de Escape: aquí se propaga SIEMPRE
   * al padre, que es quien limpia `?llevar=` de la dirección. Con la guarda `if (!this.visible) return`
   * ese aviso se perdía y al recargar el modal reaparecía (QA-07).
   */
  onVisibleChange(visible: boolean): void {
    this.visible = visible
    if (!visible) this.visibleChange.emit(false)
  }

  close(): void {
    this.onVisibleChange(false)
  }

  /**
   * `closeOnEscape` del `p-dialog` va apagado: PrimeNG cerraba el modal entero cuando Escape solo quería
   * cerrar el desplegable de votantes o el calendario (cuelgan del `body`, y el mismo evento llegaba al
   * `document`). Se perdían los arrendamientos y votantes ya elegidos (QA-11). Aquí Escape cierra el
   * modal solo si no hay ningún panel abierto.
   */
  onEscape(event: Event): void {
    if (this.submitting || hasOpenOverlay(this.document)) return
    event.preventDefault()
    this.close()
  }

  private loadMembers(): void {
    if (this.membersLoaded) {
      this.selectedVoters = this.memberOptions.map((m) => m.value)
      return
    }
    this.api.getCommitteeMembers().subscribe((r) => {
      const list = (r.data ?? []) as CommitteeMember[]
      this.memberOptions = list.map((m) => ({ label: fullName(m), value: String(m._id) }))
      this.membersLoaded = !r.error
      //* Todos preseleccionados: el comité vota completo salvo que Legal quite a alguien.
      this.selectedVoters = this.memberOptions.map((m) => m.value)
    })
  }

  /**
   * Los ids que llegaron por la dirección. Primero lo que la tabla dejó preparado en el cascarón;
   * lo que falte se pide por id al expediente —es el caso del enlace «Llevar a comité».
   */
  private loadPreselected(): void {
    const staged = this.shell.takeStaged()
    const byId = new Map(staged.map((r) => [r._id, this.toChip(r)]))
    const ids = Array.from(new Set(this.preselectedIds.filter(Boolean)))

    this.leases = ids.filter((id) => byId.has(id)).map((id) => byId.get(id) as LeaseChip)
    const missing = ids.filter((id) => !byId.has(id))
    if (!missing.length) return

    this.resolving = true
    forkJoin(missing.map((id) => this.apiLease.getById(id))).subscribe({
      next: (results) => {
        this.resolving = false
        results.forEach((r, i) => {
          //* `ILease` tipa `client` como `any` y no declara `asset`: se lee suelto, con respaldo en cada campo.
          const d: any = r?.data
          this.leases.push({
            _id: missing[i],
            folio: d?.folio ?? `…${missing[i].slice(-6)}`,
            clientName: d?.client?.name ?? d?.client?.businessName ?? '',
            assetName: d?.asset?.name ?? d?.assets?.[0]?.name ?? '',
          })
        })
      },
      error: () => {
        this.resolving = false
        //* Sin datos del expediente el chip enseña el final del id: peor dato, pero se puede enviar.
        missing.forEach((id) => this.leases.push({ _id: id, folio: `…${id.slice(-6)}`, clientName: '', assetName: '' }))
      },
    })
  }

  private toChip(row: CommitteeLeaseRow): LeaseChip {
    return { _id: row._id, folio: row.folio, clientName: row.clientName ?? '', assetName: row.assetName ?? '' }
  }
}
